/**
 * services.js — every function maps exactly to a route in routes/api.php
 * Backend base: dedicated subdomain root (set via VITE_API_BASE_URL).
 *
 * Route groups in api.php:
 *  Public:           auth/*, search, categories, salons/*
 *  Auth (sanctum):   auth/me|logout, profile/*, bookings/*, payments/*,
 *                    reviews/*, favourites/*, wallet/*, membership/*,
 *                    loyalty, notifications/*, upload/image
 *  Partner (role):   partner/salon/*, partner/services/*, partner/stylists/*,
 *                    partner/bookings/*, partner/offers/*, partner/analytics,
 *                    partner/customers, partner/payouts
 *  Admin (role):     admin/salons/*, admin/users/*, admin/reports/*
 */

import api from "./axios";
import { executeRecaptcha } from "./recaptcha";

// login / register / forgot-password auto-attach a v3 reCAPTCHA token;
// backend's enforceRecaptcha picks it up. Returns null when not configured.
const withRecaptcha = async (action, data) => {
  const recaptcha_token = await executeRecaptcha(action);
  return { ...data, recaptcha_token };
};

/**
 * Formats image URLs, handling production-to-local domain rewrites during 
 * development and providing fallbacks for broken paths.
 */
export const getImgUrl = (url) => {
  if (!url) return "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?q=80&w=800&auto=format&fit=crop"; // Salon placeholder
  
  let finalUrl = url;

  // In local development, if images are pointing to the production CDN but failing,
  // we can attempt to route them through the local backend if desired.
  // Currently, we just ensure absolute URLs are used correctly.
  if (import.meta.env.DEV && url.startsWith("/")) {
    const apiBase = import.meta.env.VITE_API_BASE_URL.replace("/api/v1", "");
    finalUrl = `${apiBase}${url}`;
  }

  // If the production image domain is used in dev but isn't working, 
  // you might want to rewrite it here. 
  // Example: finalUrl = finalUrl.replace("images.fayriza.com", "localhost:8000/storage");

  return finalUrl;
};

// ── AUTH ─────────────────────────────────────────────────────────
export const authApi = {
  sendOtp:       (identifier, isEmail=false, password, type) => api.post("/auth/send-otp", isEmail ? { email: identifier, password, type } : { phone: identifier, type }),
  verifyOtp:     (phone, otp, name)  => api.post("/auth/verify-otp",      { phone, otp, name }),
  register:      async (data)        => api.post("/auth/register",        await withRecaptcha("register", data)),
  login:         async (email, password, otp) => api.post("/auth/login",  await withRecaptcha("login", { email, password, otp })),
  logout:        ()                  => api.post("/auth/logout"),
  me:            ()                  => api.get( "/auth/me"),
  forgotPassword:async (email)       => api.post("/auth/forgot-password", await withRecaptcha("forgot_password", { email })),
  resetPassword: (data)              => api.post("/auth/reset-password",  data),
  socialLogin:   (provider, token)   => api.post("/auth/social",          { provider, token }),
};

// ── SALONS ───────────────────────────────────────────────────────
export const salonsApi = {
  list:         (params = {})         => api.get("/salons",                    { params }),
  show:         (slug)                => api.get(`/salons/${slug}`),
  images:       (id)                  => api.get(`/salons/${id}/images`),
  availability: (id, date, stylistId) => api.get(`/salons/${id}/availability`, { params: { date, stylist_id: stylistId } }),
  search:       (q, city)             => api.get("/search",                    { params: { q, city } }),
  categories:   ()                    => api.get("/categories"),
  createLead:   (data)                => api.post("/partner-leads",            data),

  partner: {
    mySalon:     ()       => api.get( "/partner/salon"),
    createSalon: (data)   => api.post("/partner/salon",            data),
    updateSalon: (data)   => api.put( "/partner/salon",            data),
    uploadImages:(form)   => api.post("/partner/salon/images",     form, { headers: { "Content-Type": "multipart/form-data" } }),
    deleteImage: (id)     => api.delete(`/partner/salon/images/${id}`),
    analytics:   ()       => api.get( "/partner/analytics"),
    customers:   (page)   => api.get( "/partner/customers",        { params: { page } }),
    payouts:     ()       => api.get( "/partner/payouts"),
    offers:      ()       => api.get( "/partner/offers"),
    createOffer: (data)   => api.post("/partner/offers",           data),
    updateOffer: (id, d)  => api.put( `/partner/offers/${id}`,     d),
    deleteOffer: (id)     => api.delete(`/partner/offers/${id}`),
  },
};

// ── SERVICES ─────────────────────────────────────────────────────
export const servicesApi = {
  bySalon:    (id, params = {}) => api.get(`/salons/${id}/services`, { params }),
  partner: {
    list:   ()       => api.get( "/partner/services"),
    create: (data)   => api.post("/partner/services",      data),
    update: (id, d)  => api.put( `/partner/services/${id}`, d),
    delete: (id)     => api.delete(`/partner/services/${id}`),
  },
};

// ── STYLISTS ─────────────────────────────────────────────────────
export const stylistsApi = {
  show:    (id) => api.get(`/stylists/${id}`),
  bySalon: (id) => api.get(`/salons/${id}/stylists`),
  partner: {
    list:           ()           => api.get( "/partner/stylists"),
    create:         (data)       => api.post("/partner/stylists",              data),
    update:         (id, d)      => api.put( `/partner/stylists/${id}`,        d),
    delete:         (id)         => api.delete(`/partner/stylists/${id}`),
    updateSchedule: (id, sched)  => api.put( `/partner/stylists/${id}/schedule`, { schedules: sched }),
  },
};

// ── BOOKINGS ─────────────────────────────────────────────────────
export const bookingsApi = {
  list:       (params = {}) => api.get(  "/bookings",                     { params }),
  show:       (ref)         => api.get(  `/bookings/${ref}`),
  create:     (data)        => api.post( "/bookings",                     data),
  cancel:     (ref, reason) => api.patch(`/bookings/${ref}/cancel`,       { reason }),
  reschedule: (ref, data)   => api.patch(`/bookings/${ref}/reschedule`,   data),
  partner: {
    list:         (params = {}) => api.get(   "/partner/bookings",                { params }),
    updateStatus: (ref, status) => api.patch( `/partner/bookings/${ref}/status`,  { status }),
  },
};

// ── PAYMENTS ─────────────────────────────────────────────────────
export const paymentsApi = {
  initiate: (booking_ref)  => api.post("/payments/initiate",           { booking_ref }),
  verify:   (data)         => api.post("/payments/verify",             data),
  refund:   (bookingRef)   => api.post(`/payments/refund/${bookingRef}`),
  order: {
    initiate: (order_ref)         => api.post("/payments/order/initiate",            { order_ref }),
    verify:   (data)              => api.post("/payments/order/verify",              data),
    refund:   (orderRef, reason)  => api.post(`/payments/order/refund/${orderRef}`,  { reason }),
  },
};

// ── REVIEWS ──────────────────────────────────────────────────────
export const reviewsApi = {
  bySalon: (id, params = {}) => api.get(   `/salons/${id}/reviews`, { params }),
  create:  (data)            => api.post(  "/reviews",               data),
  update:  (id, data)        => api.put(   `/reviews/${id}`,         data),
  delete:  (id)              => api.delete(`/reviews/${id}`),
};

// ── USER / PROFILE ────────────────────────────────────────────────
export const userApi = {
  profile:         ()     => api.get(  "/profile"),
  update:          (data) => api.put(  "/profile",         data),
  updateAvatar:    (form) => api.post( "/profile/avatar",  form, { headers: { "Content-Type": "multipart/form-data" } }),
  changePassword:  (data) => api.put(  "/profile/password", data),
  favourites:      ()     => api.get(  "/favourites"),
  toggleFavourite: (id)   => api.post( `/favourites/${id}`),
  loyaltyHistory:  ()     => api.get(  "/loyalty"),
};

// ── WALLET ────────────────────────────────────────────────────────
export const walletApi = {
  balance: ()      => api.get( "/wallet"),
  history: (page)  => api.get( "/wallet/history", { params: { page } }),
  topup:   (amount)=> api.post("/wallet/topup",   { amount }),
};

// ── MEMBERSHIP ───────────────────────────────────────────────────
export const membershipApi = {
  current:   ()                   => api.get( "/membership"),
  plans:     ()                   => api.get( "/membership/plans"),
  subscribe: (tier, payment_id)   => api.post("/membership/subscribe", { tier, payment_id }),
  cancel:    ()                   => api.post("/membership/cancel"),
};

// ── NOTIFICATIONS ────────────────────────────────────────────────
export const notificationsApi = {
  list:           (page)  => api.get(  "/notifications",       { params: { page } }),
  markRead:       (id)    => api.patch(`/notifications/${id}/read`),
  markAllRead:    ()      => api.post( "/notifications/read-all"),
  updateFcmToken: (token) => api.post( "/notifications/fcm-token", { fcm_token: token }),
};

// ── PRODUCTS ─────────────────────────────────────────────────────
export const productsApi = {
  list: (params = {}) => api.get("/products",      { params }),
  show: (id)          => api.get(`/products/${id}`),
  partner: {
    list:        ()           => api.get(    "/partner/products"),
    create:      (data)       => api.post(   "/partner/products",         data),
    update:      (id, d)      => api.put(    `/partner/products/${id}`,    d),
    delete:      (id)         => api.delete( `/partner/products/${id}`),
    adjustStock: (id, delta)  => api.patch(  `/partner/products/${id}/stock`, { delta }),
    bulkImport:  (formData)   => api.post(   "/partner/products/bulk-import", formData,
                                             { headers: { "Content-Type": "multipart/form-data" } }),
  },
};

// ── ORDERS ──────────────────────────────────────────────────────
export const ordersApi = {
  list:   (params = {}) => api.get(  "/orders",              { params }),
  show:   (ref)         => api.get(  `/orders/${ref}`),
  create: (data)        => api.post( "/orders",              data),
  cancel: (ref)         => api.patch(`/orders/${ref}/cancel`),
};

// ── COURSES ─────────────────────────────────────────────────────
export const coursesApi = {
  list:          (params = {}) => api.get(  "/courses",             { params }),
  show:          (id)          => api.get(  `/courses/${id}`),
  enroll:        (id)          => api.post( `/courses/${id}/enroll`),
  myEnrollments: ()            => api.get(  "/enrollments"),
  applyAsInstructor: (data)    => api.post( "/instructor-applications", data),
};

// ── LOYALTY ─────────────────────────────────────────────────────
export const loyaltyApi = {
  history: ()          => api.get( "/loyalty"),
  rewards: ()          => api.get( "/loyalty/rewards"),
  redeem:  (rewardId)  => api.post("/loyalty/redeem", { reward_id: rewardId }),
};

// ── UPLOAD ───────────────────────────────────────────────────────
export const uploadApi = {
  image: (file) => {
    const form = new FormData(); form.append("image", file);
    return api.post("/upload/image", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
};

// ── ADMIN ────────────────────────────────────────────────────────
export const adminApi = {
  salons:       (params = {})        => api.get(   "/admin/salons",              { params }),
  approveSalon: (id)                 => api.patch( `/admin/salons/${id}/approve`),
  suspendSalon: (id, reason)         => api.patch( `/admin/salons/${id}/suspend`, { reason }),
  users:        (params = {})        => api.get(   "/admin/users",               { params }),
  updateUser:   (id, data)           => api.patch( `/admin/users/${id}/status`,   data),
  overview:     ()                   => api.get(   "/admin/reports/overview"),
  revenue:      (params = {})        => api.get(   "/admin/reports/revenue",      { params }),
  bookings:     (params = {})        => api.get(   "/admin/reports/bookings",     { params }),
  refunds:      (params = {})        => api.get(   "/admin/refunds",              { params }),
  initiateRefund:(bookingRef, reason) => api.post(  `/admin/refunds/${bookingRef}`, { reason }),
  disputes:      (params = {})        => api.get(   "/admin/disputes",             { params }),
  updateDispute: (id, data)           => api.patch(`/admin/disputes/${id}`,      data),

  // Wave 1: audit log
  auditLogs:    (params = {})        => api.get(   "/admin/audit-logs",           { params }),
  auditStats:   (params = {})        => api.get(   "/admin/audit-logs/stats",     { params }),
  auditExportUrl:(params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return (api.defaults.baseURL || "") + "/admin/audit-logs/export" + (qs ? "?" + qs : "");
  },

  // Wave 1: system health
  systemHealth: ()                   => api.get(   "/admin/system-health"),

  // Wave 1: CSV exports (return signed URLs the browser can hit directly)
  exportUrl: (kind, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return (api.defaults.baseURL || "") + `/admin/export/${kind}.csv` + (qs ? "?" + qs : "");
  },

  // Wave 1: user impersonation
  impersonate:        (id) => api.post(  `/admin/users/${id}/impersonate`),
  revokeImpersonation:(id) => api.delete(`/admin/users/${id}/impersonations`),

  // Wave 2: moderation overview (counts for nav badges)
  moderationOverview: ()                  => api.get(  "/admin/moderation/overview"),

  // Wave 2: review moderation
  reviewQueue:        (params = {})       => api.get(  "/admin/moderation/reviews",        { params }),
  approveReview:      (id)                => api.patch(`/admin/moderation/reviews/${id}/approve`),
  rejectReview:       (id, reason)        => api.patch(`/admin/moderation/reviews/${id}/reject`,  { reason }),
  bulkReviews:        (ids, action, reason) => api.post(`/admin/moderation/reviews/bulk`, { ids, action, reason }),

  // Wave 2: photo moderation
  photoQueue:         (params = {})       => api.get(  "/admin/moderation/photos",        { params }),
  approvePhoto:       (id)                => api.patch(`/admin/moderation/photos/${id}/approve`),
  rejectPhoto:        (id, reason)        => api.patch(`/admin/moderation/photos/${id}/reject`,  { reason }),
  bulkPhotos:         (ids, action, reason) => api.post(`/admin/moderation/photos/bulk`, { ids, action, reason }),

  // Wave 2: system settings (maintenance mode + future flags)
  systemSettings:     ()                  => api.get(  "/admin/settings"),
  updateSetting:      (key, value)        => api.patch(`/admin/settings/${key}`, { value }),

  // Wave 3: integrations diagnostics
  integrations:       ()                  => api.get(  "/admin/integrations"),
  integrationSettings:()                  => api.get(  "/admin/integrations/settings"),
  saveIntegrationSetting:(key, value)     => api.post( "/admin/integrations/settings", { key, value }),
  testIntegration:    (key)               => api.post( `/admin/integrations/test/${key}`),
  testEmail:          (payload)           => api.post(  "/admin/integrations/test-email", payload),

  // Wave 3: banners CRUD
  banners:            (params = {})       => api.get(  "/admin/banners",       { params }),
  createBanner:       (data)              => api.post( "/admin/banners",        data),
  updateBanner:       (id, data)          => api.patch(`/admin/banners/${id}`,  data),
  deleteBanner:       (id)                => api.delete(`/admin/banners/${id}`),

  // Wave 3: announcements (mass email broadcast)
  announcements:        (params = {})     => api.get(  "/admin/announcements", { params }),
  announcementShow:     (id)              => api.get(  `/admin/announcements/${id}`),
  previewAnnouncement:  (data)            => api.post( "/admin/announcements/preview", data),
  storeAnnouncement:    (data)            => api.post( "/admin/announcements", data),
  sendAnnouncement:     (data)            => api.post( "/admin/announcements/send", data),
  resendAnnouncement:   (id)              => api.post( `/admin/announcements/${id}/send`),

  // Wave 4: growth analytics
  onboardingFunnel:     (params = {})     => api.get(  "/admin/analytics/onboarding-funnel",    { params }),
  cohorts:              (params = {})     => api.get(  "/admin/analytics/cohorts",              { params }),
  aiUsage:              (params = {})     => api.get(  "/admin/analytics/ai-usage",             { params }),
  emailDeliverability:  (params = {})     => api.get(  "/admin/analytics/email-deliverability", { params }),

  // Wave 5: blog / CMS
  posts:               (params = {})  => api.get(   "/admin/posts", { params }),
  postShow:            (id)           => api.get(   `/admin/posts/${id}`),
  createPost:          (data)         => api.post(  "/admin/posts", data),
  updatePost:          (id, data)     => api.patch( `/admin/posts/${id}`, data),
  deletePost:          (id)           => api.delete(`/admin/posts/${id}`),

  // Wave 5: role assignment
  roleCatalogue:       ()             => api.get(   "/admin/roles"),
  userRoles:           (userId)       => api.get(   `/admin/roles/users/${userId}`),
  assignRole:          (userId, role) => api.post(  `/admin/roles/users/${userId}`, { role }),
  revokeRole:          (userId, role) => api.delete(`/admin/roles/users/${userId}/${role}`),

  // Wave 5: webhooks
  webhooks:            (params = {})  => api.get(   "/admin/webhooks", { params }),
  webhookShow:         (id)           => api.get(   `/admin/webhooks/${id}`),
  createWebhook:       (data)         => api.post(  "/admin/webhooks", data),
  updateWebhook:       (id, data)     => api.patch( `/admin/webhooks/${id}`, data),
  deleteWebhook:       (id)           => api.delete(`/admin/webhooks/${id}`),
  rotateWebhookSecret: (id)           => api.post(  `/admin/webhooks/${id}/rotate-secret`),
  testWebhook:         (id)           => api.post(  `/admin/webhooks/${id}/test`),

  // Instructor Applications administration
  instructorApplications: (params = {}) => api.get(   "/admin/instructor-applications", { params }),
  approveInstructor:      (id)          => api.patch( `/admin/instructor-applications/${id}/approve`),
  rejectInstructor:       (id, reason)  => api.patch( `/admin/instructor-applications/${id}/reject`,  { reason }),

  // Wave 5: bulk import
  bulkImportSalons:    (file, commit = false) => {
    const fd = new FormData();
    fd.append("csv", file);
    fd.append("commit", commit ? "1" : "0");
    return api.post("/admin/bulk-import/salons", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
  },
};

// ── PUBLIC BLOG ──────────────────────────────────────────────────
export const blogApi = {
  list: (params = {}) => api.get("/posts", { params }),
  show: (slug)        => api.get(`/posts/${slug}`),
};

// ── PUBLIC SYSTEM (maintenance status banner + active site banners) ──
export const systemApi = {
  maintenance: () => api.get("/system/maintenance"),
  banners:     () => api.get("/banners"),
  myBanners:   () => api.get("/me/banners"), // role-aware, requires auth token
};

// ── AI ADVISOR ──────────────────────────────────────────────────
export const aiApi = {
  analyze:     (image, question) => api.post("/ai/analyze",      { image, question }, { timeout: 60000 }),
  chat:        (message, history)=> api.post("/ai/chat",         { message, history }, { timeout: 30000 }),
  skinRoutine: (data)            => api.post("/ai/skin-routine", data,                 { timeout: 45000 }),
};
